/**
 * Bilingual labels for the domain enums.
 *
 * `@insurensa/shared` keeps German-only labels because the API embeds them in
 * responses the German mobile app renders verbatim. The web clients localise
 * from the enum value instead, so the same lead reads "Kfz-Versicherung" or
 * "Motor insurance" depending on who is looking at it.
 */

import type { LeadType, PaymentFrequency } from '@insurensa/shared';
import type { Locale } from './locale.js';

type ByLocale<K extends string> = Record<Locale, Record<K, string>>;

export const LEAD_TYPE_LABELS: ByLocale<LeadType> = {
  de: {
    KFZ: 'Kfz-Versicherung',
    PHV: 'Privathaftpflicht',
    PKV: 'Private Krankenversicherung',
    GKV: 'Gesetzliche Krankenversicherung',
    BSV: 'Berufsunfähigkeitsversicherung',
    RS: 'Rechtsschutzversicherung',
  },
  en: {
    KFZ: 'Motor insurance',
    PHV: 'Personal liability',
    PKV: 'Private health insurance',
    GKV: 'Statutory health insurance',
    BSV: 'Occupational disability cover',
    RS: 'Legal expenses insurance',
  },
};

export const PAYMENT_FREQUENCY_LABELS: ByLocale<PaymentFrequency> = {
  de: {
    YEARLY: 'jährlich',
    SEMI_ANNUAL: 'halb-jährlich',
    QUARTERLY: 'quartalsweise',
    MONTHLY: 'monatlich',
  },
  en: {
    YEARLY: 'yearly',
    SEMI_ANNUAL: 'half-yearly',
    QUARTERLY: 'quarterly',
    MONTHLY: 'monthly',
  },
};

/** Partner approval state, as shown in the admin console. */
export const APPROVAL_LABELS: ByLocale<'PENDING' | 'APPROVED' | 'DECLINED'> = {
  de: { PENDING: 'Ausstehend', APPROVED: 'Freigegeben', DECLINED: 'Abgelehnt' },
  en: { PENDING: 'Pending', APPROVED: 'Approved', DECLINED: 'Declined' },
};

/** Review moderation state. */
export const REVIEW_LABELS: ByLocale<'PENDING' | 'APPROVED' | 'REJECTED'> = {
  de: { PENDING: 'Ausstehend', APPROVED: 'Veröffentlicht', REJECTED: 'Abgelehnt' },
  en: { PENDING: 'Pending', APPROVED: 'Published', REJECTED: 'Rejected' },
};

export const OFFER_LABELS: ByLocale<'PENDING' | 'ACCEPTED' | 'WITHDRAWN'> = {
  de: { PENDING: 'Offen', ACCEPTED: 'Angenommen', WITHDRAWN: 'Zurückgezogen' },
  en: { PENDING: 'Open', ACCEPTED: 'Accepted', WITHDRAWN: 'Withdrawn' },
};

export const ROLE_LABELS: ByLocale<'SUPER_ADMIN' | 'ADMIN' | 'MODERATOR'> = {
  de: { SUPER_ADMIN: 'Super-Admin', ADMIN: 'Administrator', MODERATOR: 'Moderator' },
  en: { SUPER_ADMIN: 'Super admin', ADMIN: 'Administrator', MODERATOR: 'Moderator' },
};

/**
 * Questionnaire fields a partner reads on a lead. The keys are the answer keys
 * the mobile app submits; several are German insurance terms with no English
 * equivalent (SF-Klasse, Beihilfe), so those keep the German term and gloss it.
 */
export const ANSWER_LABELS: ByLocale<string> = {
  de: {
    usageType: 'Nutzung',
    contractKind: 'Neu oder Wechsel',
    hsn: 'HSN',
    tsn: 'TSN',
    sfClass: 'SF-Klasse',
    sfClassComprehensive: 'SF-Klasse Vollkasko',
    annualMileageKm: 'Jährliche Fahrleistung',
    driverAge: 'Alter des Fahrers',
    insuranceScope: 'Versicherungsumfang',
    deductible: 'Selbstbeteiligung',
    paymentMethod: 'Zahlweise',
    vehicleCount: 'Anzahl Fahrzeuge',
    additionalDrivers: 'Weitere Fahrer',
    youngestDriverAge: 'Alter jüngster Fahrer',
    insuredParty: 'Zu versichernde Person',
    publicService: 'Öffentlicher Dienst',
    professionalGroup: 'Berufsgruppe',
    grossAnnualSalary: 'Bruttojahresgehalt',
    currentInsuranceStatus: 'Aktueller Versicherungsstatus',
    personAge: 'Alter',
    jobTitle: 'Beruf',
    employmentType: 'Beschäftigungsart',
    monthlyIncome: 'Monatliches Einkommen',
    moneyValidation: 'Einkommensnachweis',
    officeHours: 'Bürotätigkeit',
    policyholderAge: 'Alter Versicherungsnehmer',
    jobStatus: 'Beschäftigungsstatus',
    residentsCount: 'Anzahl Bewohner',
    annualRentalIncome: 'Jährliche Mieteinnahmen',
    coversPrivate: 'Privat-Rechtsschutz',
    coversJob: 'Berufs-Rechtsschutz',
    coversTraffic: 'Verkehrs-Rechtsschutz',
    coversResidence: 'Wohnungs-Rechtsschutz',
    coversRental: 'Vermieter-Rechtsschutz',
    parentCivilStatus: 'Familienstand der Eltern',
    parentInsurance: 'Versicherung der Eltern',
    serviceScope: 'Leistungsumfang',
    aidProvider: 'Beihilfeträger',
    subsidyRate: 'Beihilfesatz',
    adults: 'Erwachsene',
    children: 'Kinder',
    age: 'Alter',
  },
  en: {
    usageType: 'Usage',
    contractKind: 'New policy or switch',
    hsn: 'HSN (manufacturer key)',
    tsn: 'TSN (model key)',
    sfClass: 'No-claims class (SF)',
    sfClassComprehensive: 'No-claims class, comprehensive',
    annualMileageKm: 'Annual mileage',
    driverAge: 'Driver age',
    insuranceScope: 'Scope of cover',
    deductible: 'Deductible',
    paymentMethod: 'Payment frequency',
    vehicleCount: 'Number of vehicles',
    additionalDrivers: 'Additional drivers',
    youngestDriverAge: 'Youngest driver age',
    insuredParty: 'Person to be insured',
    publicService: 'Public sector employee',
    professionalGroup: 'Occupational group',
    grossAnnualSalary: 'Gross annual salary',
    currentInsuranceStatus: 'Current insurance status',
    personAge: 'Age',
    jobTitle: 'Occupation',
    employmentType: 'Type of employment',
    monthlyIncome: 'Monthly income',
    moneyValidation: 'Proof of income',
    officeHours: 'Office-based work',
    policyholderAge: 'Policyholder age',
    jobStatus: 'Employment status',
    residentsCount: 'Number of residents',
    annualRentalIncome: 'Annual rental income',
    coversPrivate: 'Private legal cover',
    coversJob: 'Employment legal cover',
    coversTraffic: 'Motor legal cover',
    coversResidence: 'Residential legal cover',
    coversRental: 'Landlord legal cover',
    parentCivilStatus: "Parents' marital status",
    parentInsurance: "Parents' insurer",
    serviceScope: 'Scope of benefits',
    aidProvider: 'Civil-service subsidy body (Beihilfe)',
    subsidyRate: 'Subsidy rate',
    adults: 'Adults',
    children: 'Children',
    age: 'Age',
  },
};
